"use client";

import {JSX, useEffect} from "react";

import dynamic from "next/dynamic";

import {PropertyListModel} from "@/features/rental/property-list/property-list.model";

import type {Rental} from "@/entities/rental";

import {useMatchMedia} from "@/shared/hooks/dom/use-match-media";
import {useModel} from "@/shared/hooks/state-management/use-model";

import type {OurPropertySectionData} from "./type";

// Dynamic imports for different UI components
const OurProperty = dynamic(() => import("@/sections/rental/properties-list/our-property").then((mod) => ({default: mod.OurProperty})));

const OurPropertyV2 = dynamic(() => import("@/sections/rental/properties-list/our-property-v2").then((mod) => ({default: mod.OurPropertyV2})));

const RentalOfferShuffle = dynamic(() => import("@/sections/rental/rental-offer/variants").then((mod) => ({default: mod.RentalOfferShuffle})));

interface OurPropertyClientProps {
    data: OurPropertySectionData;
    initialPlaces?: Array<Rental>;
    initialTotal?: number;
    initialPages?: Array<Array<string>>;
    initialSearchUrl?: string | null;
    anchorId?: string;
    showCount?: number;
}

export function OurPropertyClient({
    data,
    initialPlaces,
    initialTotal,
    initialPages,
    initialSearchUrl,
    anchorId,
    showCount = 8,
}: OurPropertyClientProps): JSX.Element {
    // Create model using useModel hook - it returns a proxy
    const [model] = useModel(PropertyListModel, {
        sectionData: data,
        initialPlaces: initialPlaces || [],
        initialTotal: initialTotal || 0,
        initialPages: initialPages || [],
        initialSearchUrl: initialSearchUrl,
        showCount,
    });

    // Responsive items count: 6 items on tablet (<=991px), default on mobile (<=767px) and desktop
    const isTablet = useMatchMedia({maxWidth: 991});
    const isMobile = useMatchMedia({maxWidth: 767});

    useEffect(() => {
        if (data.sectionType === "suggestions") return;

        if (isTablet && !isMobile) {
            model.setItemsPerPage(6);
        } else {
            model.setItemsPerPage(showCount);
        }
    }, [isTablet, isMobile, data.sectionType]);

    if (data.designVersion === "v2" && data.sectionType === "suggestions") {
        return (
            <OurPropertyV2
                title={data.title}
                subtitle={data.subtitle}
                properties={model.visibleProperties}
                anchorId={anchorId}
                loading={{
                    isFiltering: model.isFiltering,
                    isInitial: model.isLoading,
                }}
                total={model.total}
                viewAllUrl={data.viewAllLink?.url || model.viewAllUrl}
                viewAllText={data.viewAllLink?.text}
                space={data.space}
            />
        );
    }

    // Render different UI based on section type
    if (data.sectionType === "suggestions") {
        return (
            <RentalOfferShuffle
                properties={model.visibleProperties}
                loading={{
                    isFiltering: model.isFiltering,
                    isInitial: model.isLoading,
                }}
                anchorId={anchorId}
                showShuffleButton={model.showShuffleButton}
                onShuffle={() => model.shuffle()}
                space={data.space}
                content={{
                    title: data.title,
                    subtitle: data.subtitle,
                }}
                viewAllLink={data.viewAllLink}
            />
        );
    }

    if (data.designVersion === "v2") {
        return (
            <OurPropertyV2
                title={data.title}
                properties={model.visibleProperties}
                tags={model.tagsWithHandlers}
                anchorId={anchorId}
                loading={{
                    isFiltering: model.isFiltering,
                    isInitial: model.isLoading,
                }}
                total={model.total}
                viewAllUrl={model.viewAllUrl}
                onToggleTag={(tagValue) => model.toggleTag(tagValue)}
                space={data.space}
            />
        );
    }

    // Default: search mode
    return (
        <OurProperty
            properties={model.visibleProperties}
            tags={model.tagsWithHandlers}
            anchorId={anchorId}
            loading={{
                isFiltering: model.isFiltering,
                isInitial: model.isLoading,
                isPagination: model.isPaginationLoading,
            }}
            showButton={model.showButton}
            showReadLessButton={model.showReadLessButton}
            onToggleTag={(tagValue) => model.toggleTag(tagValue)}
            onLoadMore={() => void model.loadMore()}
            onShowLess={() => model.showLess()}
            space={data.space}
        />
    );
}
